import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { sortBy } from 'underscore';
import { showToast } from 'stores/ToastStore';
import { getAdminUsers } from 'utils/UserWebAPIUtils';
import { getCustomerV2, updateShipper } from 'utils/CustomersAPIUtils';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import Spinner from 'components/ui/Spinner';
import ShipperSettings from '../ShipperSettings';
import Commercial from './Commercial';
import ShorthaulOperations from './ShorthaulOperations';
import DrayageOperations from './DrayageOperations';
import BillingTeam from '../billingv2/BillingTeam';
import UserActionCreators from 'actions/UserActionCreators';
import store from 'stores/CustomerDetailsStore';
import CustomersActionCreators from 'actions/CustomersActionCreators';

const Team = () => {
  const { t } = useTranslation();
  const { id: userId } = useParams();
  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeAdmins, setActiveAdmins] = useState([]);
  const [activeSalesReps, setSalesReps] = useState([]);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [isSalesRepsLoading, setIsSalesRepsLoading] = useState(false);
  const [commercialData, setCommercialData] = useState({});
  const [shorthaulOpsData, setShorthaulOpsData] = useState({});
  const [drayageOpsData, setDrayageOpsData] = useState({});

  const fetchAdmins = async () => {
    try {
      setIsAdminLoading(true);
      const admins = await getAdminUsers({ active: true });
      if (admins) {
        const adminPeople = admins.entity.data;
        setActiveAdmins(
          sortBy(
            adminPeople.map((a) => {
              const value = a.email.trim();
              return { name: value, slug: value };
            }),
            'name',
          ),
        );
      }
      setIsAdminLoading(false);
    } catch (e) {
      setIsAdminLoading(false);
      showToast(t('SHIPPER'), t('FAILED_FETCH_ADMINS'), 'error');
    }
  };

  const fetchSalesReps = async () => {
    try {
      setIsSalesRepsLoading(true);
      const salesReps = await getAdminUsers({ sales: true, active: true });
      if (salesReps) {
        const salesPeople = salesReps.entity.data;
        setSalesReps(
          salesPeople.map((s) => {
            const value = s.email.trim();
            return { name: value, slug: value };
          }),
        );
      }
      setIsSalesRepsLoading(false);
    } catch (e) {
      setIsSalesRepsLoading(false);
      showToast(t('SHIPPER'), t('FAILED_FETCH_SALES_REPS'), 'error');
    }
  };

  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const user = await getCustomerV2(userId);
      setUser(user);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showToast(t('SHIPPER'), t('FETCH_FAILED_SHIPPER_DATA'), 'error');
    }
  };

  const renderSidebar = () => {
    return <ShipperSettings userId={userId} activePage="team" />;
  };

  const breadcrumbs = {
    pages: [
      {
        name: user?.company_name || t('SHIPPER'),
        href: `/users/${userId}`,
        current: false,
      },
      {
        name: t('TEAM'),
        href: `/users/${userId}/settings/team`,
        current: true,
      },
    ],
  };

  useEffect(() => {
    store.addChangeListener(fetchUser);
    store.addChangeListener(fetchAdmins);
    store.addChangeListener(fetchSalesReps);
    CustomersActionCreators.getCustomer(userId);
    UserActionCreators.getAdminUsers({ active: true });
    return () => {
      store.removeChangeListener(fetchUser);
      store.removeChangeListener(fetchAdmins);
      store.removeChangeListener(fetchSalesReps);
    };
  }, []);

  const noneCheck = (obj = {}) => {
    const data = { ...obj };
    Object.keys(obj).forEach((k) => {
      if (obj[k] === 'none') {
        data[k] = '';
      }
    });
    return data;
  };

  const getCommercialData = (currentUser, salesReps = [], admins = []) => {
    const { sales_rep = '', account_manager = '' } = currentUser;
    const data = noneCheck({
      sales_rep,
      account_manager,
      salesReps,
    });
    setCommercialData({ ...data, admins });
  };

  const getShorthaulOpsData = (currentUser, admins = []) => {
    const { operations_manager, customer_success_manager } = currentUser;
    const data = noneCheck({
      operations_manager,
      customer_success_manager,
    });
    setShorthaulOpsData({ ...data, admins });
  };

  const getDrayageOpsData = (currentUser, admins = []) => {
    const {
      data_entry_agent,
      customer_success_west,
      customer_success_central,
      customer_success_northeast,
      customer_success_southeast,
      customer_success_exports,
    } = currentUser;
    const data = noneCheck({
      data_entry_agent,
      customer_success_west,
      customer_success_central,
      customer_success_northeast,
      customer_success_southeast,
      customer_success_exports,
    });
    setDrayageOpsData({
      ...data,
      admins,
    });
  };

  const onSaveShipper = async (updates = {}) => {
    const payload = { _id: userId, ...updates };
    await updateShipper(userId, payload);
  };
  const loading = isLoading || isAdminLoading || isSalesRepsLoading;

  useEffect(() => {
    if (user && activeAdmins.length && activeSalesReps.length) {
      getCommercialData(user, activeSalesReps, activeAdmins);
      getShorthaulOpsData(user, activeAdmins);
      getDrayageOpsData(user, activeAdmins);
    }
  }, [user, activeAdmins, activeSalesReps]);

  const getBillingTeamData = () => {
    const { billing_rep, billing_lead, collector } = user;
    return { billing_rep, billing_lead, collector };
  };

  return (
    <LayoutWithSidebar
      title={t('TEAM')}
      breadcrumbs={breadcrumbs}
      sidebar={renderSidebar}
      className="py-4"
    >
      {loading ? (
        <Spinner showMessage={false} />
      ) : (
        <div
          data-testid="shipper-team"
          className="lg:grid lg:grid-cols-2 gap-4 focus-within:lg:gap-4"
        >
          <Commercial
            className="lg:mb-0 mb-4"
            data={commercialData}
            onUpdateShipper={onSaveShipper}
          />
          <ShorthaulOperations
            className="lg:mb-0 mb-4"
            data={shorthaulOpsData}
            onUpdateShipper={onSaveShipper}
          />
          <DrayageOperations
            data={drayageOpsData}
            onUpdateShipper={onSaveShipper}
          />
          <BillingTeam
            data={getBillingTeamData()}
            onUpdateShipper={onSaveShipper}
          />
        </div>
      )}
    </LayoutWithSidebar>
  );
};

export default Team;
